import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import { FaHeart, FaLanguage, FaBook, FaPlay, FaStar, FaFire, FaClock, FaTrophy, FaLock, FaUnlock } from 'react-icons/fa';

interface Video {
  id: string;
  video_url: string;
  thumbnail_url: string;
  title: string;
  description: string;
  category: string;
  language: string;
  likes_count: number;
  is_promoted: boolean;
  is_paid: boolean;
  price: number;
}

export default function Dashboard() {
  const [promotedVideos, setPromotedVideos] = useState<Video[]>([]);
  const [paidVideos, setPaidVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = getCurrentUser();

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      setLoading(true);

      // Load promoted videos
      const { data: promotedData, error: promotedError } = await supabase
        .from('ponavideos')
        .select('*')
        .eq('is_visible', true)
        .eq('is_promoted', true)
        .limit(6);

      if (promotedError) throw promotedError;

      // Load paid videos
      const { data: paidData, error: paidError } = await supabase
        .from('ponavideos')
        .select('*')
        .eq('is_visible', true)
        .eq('is_paid', true)
        .limit(6);

      if (paidError) throw paidError;

      setPromotedVideos(promotedData || []);
      setPaidVideos(paidData || []);
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (videoId: string) => {
    navigate(`/video/${videoId}`);
  };

  return (
    <div className="min-h-screen bg-black text-white py-8">
      {/* Welcome Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Welcome to Sexual X</h1>
          <p className="text-xl text-gray-400">
            Discover our premium and promoted content
          </p>
        </div>

        {/* Promoted Videos Section */}
        <div className="mb-12">
          <div className="flex items-center mb-6">
            <FaFire className="w-6 h-6 text-yellow-500 mr-2" />
            <h2 className="text-2xl font-bold">Promoted Videos</h2>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-900 rounded-lg h-64 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {promotedVideos.map((video) => (
                <div 
                  key={video.id} 
                  className="bg-gray-900 rounded-lg overflow-hidden cursor-pointer transform hover:scale-105 transition-transform duration-200"
                  onClick={() => handleVideoClick(video.id)}
                >
                  <div className="relative h-48">
                    <img
                      src={video.thumbnail_url}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-yellow-500 text-black px-2 py-1 rounded-full text-xs font-medium">
                      Promoted
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold mb-2">{video.title}</h3>
                    <div className="flex items-center justify-between text-sm text-gray-400">
                      <div className="flex items-center">
                        <FaLanguage className="mr-1" />
                        {video.language}
                      </div>
                      <div className="flex items-center">
                        <FaHeart className="mr-1" />
                        {video.likes_count}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Premium Videos Section */}
        <div>
          <div className="flex items-center mb-6">
            <FaStar className="w-6 h-6 text-yellow-500 mr-2" />
            <h2 className="text-2xl font-bold">Premium Videos</h2>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-900 rounded-lg h-64 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paidVideos.map((video) => (
                <div 
                  key={video.id} 
                  className="bg-gray-900 rounded-lg overflow-hidden cursor-pointer transform hover:scale-105 transition-transform duration-200"
                  onClick={() => handleVideoClick(video.id)}
                >
                  <div className="relative h-48">
                    <img
                      src={video.thumbnail_url}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      ${video.price}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold mb-2">{video.title}</h3>
                    <div className="flex items-center justify-between text-sm text-gray-400">
                      <div className="flex items-center">
                        <FaLanguage className="mr-1" />
                        {video.language}
                      </div>
                      <div className="flex items-center">
                        <FaHeart className="mr-1" />
                        {video.likes_count}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}